/**
 * 차트 범례·축에 쓰는 표기 함수. (2026-09-20 사용자 피드백, BACKLOG I-73)
 *
 * 왜 CandleChart 밖으로 뺐나
 *   CandleChart 는 캔버스를 다루는 명령형 컴포넌트라 jsdom 에서 렌더가 안 된다
 *   (lightweight-charts 가 canvas 컨텍스트를 요구한다). 표기 규칙만 떼어 두면
 *   Vitest 로 값을 직접 검증할 수 있다.
 *
 * 🔴 여기의 함수는 전부 **문자열을 문자열로** 바꾼다. Date 로 파싱하지 않는다
 *   서버가 KST 로 확정해 'yyyy-MM-dd' 와 epochSecond 를 둘 다 내려준다.
 *   날짜만 있는 문자열을 new Date() 에 넣으면 UTC 자정으로 읽혀 한국에서
 *   하루 밀린다 (lib/format.ts formatDate 주석과 같은 함정).
 */

/** 값이 없을 때 쓰는 표기. lib/format.ts 와 같은 기호를 쓴다. */
const EMPTY = '-';

/**
 * 일봉 날짜를 '2026년 8월 10일' 로 바꾼다. (2026-09-20 사용자 요청)
 *
 * 바꾸기 전에는 lightweight-charts 기본값이 나와 `10 8월 '26` 으로 보였다.
 * 라이브러리가 영어권 순서(일-월-연)에 한국어 월 이름을 섞은 결과라
 * 읽는 순간 한 번 멈칫하게 된다.
 *
 * 월·일의 0 을 떼는 이유 - '2026년 08월 10일' 은 기계가 찍은 느낌이고
 * 사람이 읽는 자리에서는 앞의 0 이 정보를 더하지 않는다.
 *
 * @param date 'yyyy-MM-dd'. 그 형태가 아니면 '-' 를 준다
 */
export function formatCandleDate(date: string | null | undefined): string {
  if (!date) {
    return EMPTY;
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
  if (!match) {
    return EMPTY;
  }
  return `${match[1]}년 ${Number(match[2])}월 ${Number(match[3])}일`;
}

/**
 * 주봉 · 월봉 범례의 날짜 (2026-09-29 I-70).
 *   주봉  "2026년 9월 21일 주" — 봉 날짜는 그 주의 첫 거래일이다(월요일 휴장이면 화요일 · CandleAggregator)
 *   월봉  "2026년 9월"         — 날짜(일)를 붙이면 첫 거래일로 오해한다
 *   그 밖은 formatCandleDate 와 같다
 */
export function formatCandlePeriod(date: string | null | undefined, interval: '1w' | '1mo' | '1d'): string {
  const day = formatCandleDate(date);
  if (day === EMPTY || interval === '1d') {
    return day;
  }
  if (interval === '1w') {
    return `${day} 주`;
  }
  return day.replace(/ \d+일$/, '');
}

/**
 * 이 주봉 · 월봉이 아직 끝나지 않은 구간인가 — 오늘(KST)이 그 주(월요일 시작) · 그 달에 들어 있나. (2026-09-29 I-70)
 * 🔴 진행 중인 구간의 봉은 오늘까지만 묶인 미완성 값이다 — 범례가 "진행 중" 을 붙여 완성된 봉처럼 읽히지 않게 한다(BACKLOG I-70 9/20 메모)
 *
 * @param date 봉 날짜(구간 첫 거래일 · YYYY-MM-DD)
 * @param now  기준 시각(테스트용). KST 로 바꿔 날짜를 잡는다
 */
export function isOpenPeriod(date: string | null | undefined, interval: '1w' | '1mo', now: Date = new Date()): boolean {
  const match = date ? /^(\d{4})-(\d{2})-(\d{2})/.exec(date) : null;
  if (!match) {
    return false;
  }
  const kst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
  const ty = kst.getUTCFullYear();
  const tm = kst.getUTCMonth() + 1;
  const td = kst.getUTCDate();
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  if (interval === '1mo') {
    return y === ty && m === tm;
  }
  // 주 = 월요일 시작. 두 날짜의 "그 주 월요일" 이 같으면 같은 주다(UTC 자정 기준 일수로 셈 — 서머타임 영향 없음)
  const mondayOf = (yy: number, mm: number, dd: number) => {
    const day = Date.UTC(yy, mm - 1, dd) / 86400000;
    const weekday = new Date(Date.UTC(yy, mm - 1, dd)).getUTCDay();   // 0 일 ~ 6 토
    return day - ((weekday + 6) % 7);
  };
  return mondayOf(y, m, d) === mondayOf(ty, tm, td);
}

/** KST 오프셋(초). 한국은 서머타임이 없어 늘 +9 다. */
const KST_OFFSET_SECONDS = 9 * 60 * 60;

/**
 * 분봉 시각을 '8월 10일 14:32' 로 바꾼다.
 *
 * 연도를 빼는 이유 - 분봉은 최대 하루치라(StockDetailModal 안내 문구)
 * 연도가 늘 오늘이다. 좁은 범례 줄에서 자리만 차지한다.
 *
 * 🔴 epochSecond 는 **진짜 UNIX 초**다. 이미 KST 인 값이 아니다
 *   `CandleResponse.from` 이 `timestamp().toEpochSecond()` 로 만든다 -
 *   오프셋(+09:00)을 존중해 계산한 실제 순간이다.
 *   그래서 KST 09:31 봉의 epochSecond 는 UTC 00:31 에 해당한다.
 *
 * 🔴 브라우저 시간대(`getHours`)도 쓰지 않는다
 *   해외에서 접속하면 09:31 봉이 다른 시각으로 보인다. 우리 시각 표기는
 *   전부 KST 고정이다(CLAUDE.md 5.3절 - 시각은 명시적 ZoneId).
 *   그래서 9시간을 더한 뒤 UTC 로 읽어 KST 를 얻는다. 이 방식은
 *   서버·브라우저 시간대가 무엇이든 같은 값을 준다.
 *
 * @param epochSecond 봉 시작 시각의 UNIX 초 (실제 순간)
 */
export function formatCandleMinute(epochSecond: number | null | undefined): string {
  if (epochSecond === null || epochSecond === undefined || !Number.isFinite(epochSecond)) {
    return EMPTY;
  }
  const kst = new Date((epochSecond + KST_OFFSET_SECONDS) * 1000);
  const month = kst.getUTCMonth() + 1;
  const day = kst.getUTCDate();
  const hour = String(kst.getUTCHours()).padStart(2, '0');
  const minute = String(kst.getUTCMinutes()).padStart(2, '0');
  return `${month}월 ${day}일 ${hour}:${minute}`;
}

/**
 * 분봉의 실제 UNIX 초 → 차트에 넣을 시각. (2026-09-29 I-74)
 *
 * 🔴 왜 9시간을 더하나 — lightweight-charts 는 시각을 **UTC 로 읽어 그대로** 그린다
 *   원본 확인(5.2.1 `dist/lightweight-charts.development.mjs` 기본 눈금 포맷 — `getUTCHours()` 로 뽑은 값을
 *   로컬 Date 로 옮겨 찍는다 · 날짜 경계 `weightByTime` 도 `getUTCDate()`). 시간대 옵션은 없다.
 *   그래서 KST 09:31 봉(UTC 00:31)이 축에 00:31 로 보였고, 여러 날 분봉의 날짜 눈금이 KST 09:00 에 섰다
 *   (08:00~09:00 NXT 봉이 전날로 묶였다). 라이브러리 문서가 권하는 방법대로 시각을 KST 벽시계로 밀어 넣는다.
 *
 * 🔴 차트 밖으로 이 값을 내보내지 않는다 — 범례 · 봉 찾기는 fromChartMinuteTime 으로 되돌려 쓴다
 *   (되돌리지 않고 formatCandleMinute 에 넣으면 9시간이 두 번 더해진다)
 */
export function toChartMinuteTime(epochSecond: number): number {
  return epochSecond + KST_OFFSET_SECONDS;
}

/** toChartMinuteTime 의 반대 — 크로스헤어가 돌려준 차트 시각을 실제 UNIX 초로 */
export function fromChartMinuteTime(chartTime: number): number {
  return chartTime - KST_OFFSET_SECONDS;
}

/**
 * 거래량을 읽기 쉽게 끊는다.
 *
 * 🔴 왜 만들었나 (2026-09-20 사용자 질문 - "저게 고점을 의미하는건가")
 *   차트 오른쪽 아래에 16,971,358 이 설명 없이 떠 있었다. 거래량 시리즈의
 *   마지막 값 라벨인데, 종가(256,000)·크로스헤어 가격(231,500)과 같은
 *   가격축에 찍혀 가격으로 읽혔다. 라벨을 끄고 이름과 함께 범례에 옮겼다.
 *
 * 콤마를 그대로 두는 이유 - '1,697만' 으로 줄이면 자릿수 감각이 사라진다.
 * 범례는 폭이 넉넉해 전체를 적을 수 있다. 축약은 좁은 자리에서만 쓴다.
 */
export function formatVolume(volume: number | null | undefined): string {
  if (volume === null || volume === undefined || !Number.isFinite(volume)) {
    return EMPTY;
  }
  return Math.round(volume).toLocaleString('ko-KR');
}

/**
 * 범례에 찍을 가격. 국내는 정수 + 콤마, 미국은 소수 2자리다.
 *
 * CandleChart 의 priceFormatter 와 같은 규칙이어야 한다 -
 * 같은 봉의 종가가 축과 범례에서 다르게 보이면 어느 쪽을 믿을지 알 수 없다.
 * (CLAUDE.md 6.1절이 format.ts 와 MoneyUtils 에 요구하는 것과 같은 원칙)
 */
export function formatCandlePrice(
  price: number | null | undefined,
  us: boolean,
): string {
  if (price === null || price === undefined || !Number.isFinite(price)) {
    return EMPTY;
  }
  return us
    ? price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : Math.round(price).toLocaleString('ko-KR');
}
